import React, { useEffect, useMemo, useState } from "react";
import { AdminTable } from "../Table/AntTable";
import type { DataTableColumn, ActionConfig } from "../Table/AntTable";
import { message } from "antd";
import { CustomChallenge } from "../weeklyChallenges/customChallenge";
import { AdminDeleteModal } from "../AdminDeleteModal";
import { deleteBlog, getBlogCategories, getBlogs } from "../../services/firebase";
import { BlogCategoryType, BlogType } from "../../types/blog";
import EditBlogModal from "../Blog/EditBlogModal";

const ViewArticles: React.FC = () => {
  const [data, setData] = useState<BlogType[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);
  const [deletingBlogId, setDeletingBlogId] = useState<string | null>(null);
  const [blogCategories, setBlogCategories] = useState<BlogCategoryType[]>([]);

   const fetchBlogCategories = async () => {
      try {
        const blogCategoryList = await getBlogCategories();
        //sorting as per display order
        blogCategoryList.sort(
          (a, b) => a.blog_category_display_order - b.blog_category_display_order
        );
        setBlogCategories(blogCategoryList);
      } catch (error) {
        console.error("Failed to fetch blog categories:", error);
      }
    };
    useEffect(() => {
      fetchBlogCategories();
    }, []);

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    blogCategories.forEach((c) => {
      map.set(c.blog_category_id, c.blog_category_name);
    });
    return map;
  }, [blogCategories]);

  const columns: DataTableColumn<BlogType>[] = [
    {
      title: "Image",
      dataIndex: "image",
      sorter: true,
      width: 100,
      render: (value: string) => {
        if (!value) return null;
        return (
          <img
            src={value}
            alt="article-image"
            style={{
              width: "80px",
              height: "80px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Author",
      dataIndex: "author",
      sorter: true,
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      title: "Category",
      dataIndex: "blogCategoryId",
      sorter: (a, b) => {
        const nameA = categoryNameById.get(a.blogCategoryId) || "";
        const nameB = categoryNameById.get(b.blogCategoryId) || "";
        return nameA.localeCompare(nameB);
      },
      render: (value: number) => {
        const name = categoryNameById.get(value);
        return <span>{name || "-"}</span>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 120,
      sorter: (a, b) => {
        const getTime = (value: any) => {
          if (!value) return 0;
          if (value.toDate) return value.toDate().getTime();
          if (value.seconds) return value.seconds * 1000;
          if (value instanceof Date) return value.getTime();
          return 0;
        };
        return getTime(a.createdAt) - getTime(b.createdAt);
      },
      render: (value: any) => {
        if (!value) return "-";
        try {
          let date: Date;
          if (value.toDate) {
            date = value.toDate();
          } else if (value.seconds) {
            date = new Date(value.seconds * 1000);
          } else if (value instanceof Date) {
            date = value;
          } else {
            date = new Date(value);
          }
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return "-";
        }
      },
    },
  ];

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const fetched = await getBlogs();
      console.log(fetched);
      setData(fetched);
      //setAllChallenges(fetched.sort((a, b) => b.year.localeCompare(a.year)));
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Define action handlers
  const handleEdit = (record: BlogType) => {
    message.info(`Editing blog: ${record.title}`);
    if (record.id) {
      setEditingBlogId(record.id);
    }
  };

  const handleDelete = (record: BlogType) => {
    if (record.id) {
      setDeletingBlogId(record.id);
    }
  };

  const onDeleteBlog = async (blogId: string) => {
    try {
      await deleteBlog(blogId);
      message.success("Blog deleted successfully");
      setDeletingBlogId(null);
      fetchBlogs();
    } catch (error) {
      console.error("Failed to delete blog:", error);
    }
  };

  const actions: ActionConfig<BlogType> = {
    onEdit: handleEdit,
    onDelete: handleDelete,
    editText: "Edit",
    deleteText: "Delete",
  };

  return (
    <div style={{ padding: "24px" }}>
      <AdminTable<BlogType>
        columns={columns}
        data={data}
        rowKey="id"
        loading={loading}
        actions={actions}
        pageSize={10}
        searchable={true}
        searchPlaceholder="Search users by any field..."
        bordered={true}
        size="middle"
        showSizeChanger={true}
        pageSizeOptions={[10, 20, 50, 100]}
      />
      {editingBlogId && (
        <EditBlogModal
          blogId={editingBlogId}
          isAdmin={true}
          onSaveSuccess={() => {
            message.success("Blog updated successfully");
            setEditingBlogId(null);
            fetchBlogs();
          }}
          isEdit={true}
          onClose={() => setEditingBlogId(null)}
        />
      )}
      {deletingBlogId && (
        <AdminDeleteModal
          onConfirm={() => onDeleteBlog(deletingBlogId)}
          description="Are you sure you want to delete this blog? This action cannot be undone."
          isDeleting={true}
          onClose={() => setDeletingBlogId(null)}
        />
      )}
    </div>
  );
};

export default ViewArticles;
